import type { DBImportJob } from "@KassaroAB/shared/types/importJob";
import type {
  RestBulkUpdateProducts,
  RestBulkUpdateProductsResponse,
  RestCreateProduct,
  RestProduct,
  RestUpdateProduct,
} from "@KassaroAB/shared/types/product";
import { auth } from "../config/firebase";
import { API_BASE_URL } from "../constants/api";
import { api } from "../utils/api";
import type {
  ImportJobId,
  ProductId,
} from "@KassaroAB/shared/types/ids";
import type { Money } from "@KassaroAB/shared/types/money";
import type { ProductVariant } from "@KassaroAB/shared/types/productVariant";
import {
  catalogCollectionPath,
  type CatalogScope,
} from "./catalogScope";

const productsCollectionPath = (scope: CatalogScope) =>
  catalogCollectionPath(scope, "products");

export const getProductsApi = (scope: CatalogScope): Promise<RestProduct[]> => {
  return api.get<RestProduct[]>(productsCollectionPath(scope));
};

export interface GetProductsPageParams {
  updatedSince?: string;
  cursor?: ProductId;
  limit?: number;
}

export const getProductsPageApi = (
  scope: CatalogScope,
  params: GetProductsPageParams,
): Promise<RestProduct[]> => {
  const query = new URLSearchParams();
  if (params.updatedSince) {
    query.set("updatedSince", params.updatedSince);
  }
  if (params.cursor) {
    query.set("cursor", params.cursor);
  }
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  const queryString = query.toString();
  return api.get<RestProduct[]>(
    `${productsCollectionPath(scope)}${queryString ? `?${queryString}` : ""}`,
  );
};

export const bulkUpdateProductsApi = (
  scope: CatalogScope,
  updates: RestBulkUpdateProducts["updates"],
): Promise<RestBulkUpdateProductsResponse> => {
  return api.put<RestBulkUpdateProductsResponse>(
    `${productsCollectionPath(scope)}/bulk`,
    { updates },
  );
};

export const getProductApi = (
  scope: CatalogScope,
  productId: ProductId,
): Promise<RestProduct> => {
  return api.get<RestProduct>(
    `${productsCollectionPath(scope)}/${productId}`,
  );
};

export const createProductApi = (
  scope: CatalogScope,
  product: RestCreateProduct,
): Promise<RestProduct> => {
  return api.post<RestProduct>(productsCollectionPath(scope), product);
};

export const updateProductApi = (
  scope: CatalogScope,
  productId: ProductId,
  product: RestUpdateProduct,
): Promise<RestProduct> => {
  return api.put<RestProduct>(
    `${productsCollectionPath(scope)}/${productId}`,
    product,
  );
};

export const deleteProductApi = (
  scope: CatalogScope,
  productId: ProductId,
): Promise<void> => {
  return api.delete<void>(`${productsCollectionPath(scope)}/${productId}`);
};



export interface ImportMatchFields {
  id: boolean;
  articleNumber: boolean;
  barcode: boolean;
  variantId: boolean;
  extraFields: boolean;
}

export const DEFAULT_IMPORT_MATCH_FIELDS: ImportMatchFields = {
  id: true,
  articleNumber: true,
  barcode: true,
  variantId: true,
  extraFields: true,
};

export const toImportMatchFieldsPayload = (
  fields: ImportMatchFields
): string[] => {
  const payload: string[] = [];
  if (fields.id) {
    payload.push("ID");
  }
  if (fields.articleNumber) {
    payload.push("ARTICLE_NUMBER");
  }
  if (fields.barcode) {
    payload.push("BARCODE");
  }
  if (fields.variantId) {
    payload.push("VARIANT_ID");
  }
  if (fields.extraFields) {
    payload.push("EXTRA_FIELDS");
  }
  return payload;
};

export interface ImportProductsParams {
  columnMapping: string[];
  csvFile: File;
  defaultValues?: Record<string, string>;
  skipFirstRow: boolean;
  encoding: string;
  delimiter: string;
  dryrun: boolean;
  onlyUpdate: boolean;
  variantGroupColumn?: string;
  firstRowAsVariant?: boolean;
  matchFields?: string[];
  addStock?: boolean;
}

export interface ImportValidProduct {
  id: string | null;
  name: string;
  unitPrice: Money;
  VAT: number;
  articleNumber: string;
  barcode:  null | { code: string; type: string; format: string };
  unitId: string | null;
  openPrice: boolean;
  group: null | { id: string; name: string };
  bookkeepingAccount: null | { id: string; number: string };
  stockCount: number | null;
  bongLocationId: string | null;
  requireReference: boolean;
  type: string;
  deviations: unknown[];
  purchasePrice: Money | null;
  takeawayTax?: number | null;
  takeawayPrice?: Money | null;
  takeawayBookkeepingAccount?: null | { id: string; number: string };
  extraProductFieldValues?: Record<string, string> | null;
  variants?: ProductVariant[];
}

export interface ImportError {
  row: number;
  field: string;
  message: string;
  value: string;
}

export interface ImportDryRunResults {
  success: boolean;
  totalRows: number;
  validRows: number;
  errorCount: number;
  errors?: ImportError[];
  validProducts?: ImportValidProduct[];
}

export type ImportProductsResponse = ImportDryRunResults | {
  importJobId?: string;
};

export const importProductsApi = async (
  scope: CatalogScope,
  params: ImportProductsParams
): Promise<ImportProductsResponse | null> => {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  return auth.currentUser
    .getIdToken()
    .then((token) => {
      const formData = new FormData();
      formData.append("columnMapping", JSON.stringify(params.columnMapping));
      formData.append("csvFile", params.csvFile);
      if (params.defaultValues) {
        formData.append("defaultValues", JSON.stringify(params.defaultValues));
      }
      formData.append("skipFirstRow", params.skipFirstRow ? "true" : "false");
      formData.append("encoding", params.encoding);
      formData.append("delimiter", params.delimiter);
      formData.append("dryrun", params.dryrun ? "true" : "false");
      formData.append("onlyUpdate", params.onlyUpdate ? "true" : "false");
      formData.append("addStock", params.addStock ? "true" : "false");
      if (params.variantGroupColumn) {
        formData.append("variantGroupColumn", params.variantGroupColumn);
      }
      if (params.firstRowAsVariant !== undefined) {
        formData.append("firstRowAsVariant", params.firstRowAsVariant ? "true" : "false");
      }
      if (params.matchFields) {
        formData.append("matchFields", JSON.stringify(params.matchFields));
      }

      const headers = new Headers();
      if (token) {
        headers.append("Authorization", `Bearer ${token}`);
      }

      return fetch(`${API_BASE_URL}/${catalogCollectionPath(scope, "products/import")}`, {
        method: "POST",
        headers,
        body: formData,
      });
    })
    .then((response) => {
      if (!response.ok) {
        return response
          .json()
          .then((data) => {
            throw new Error(data.message || `HTTP error ${response.status}`);
          })
          .catch((err) => {
            throw new Error(err.message || `HTTP error ${response.status}`);
          });
      }
      if (response.status === 204) {
        return null;
      }
      return response.json();
    });
};

export const getImportJobApi = async (
  scope: CatalogScope,
  importJobId: ImportJobId
): Promise<DBImportJob> => {
  return api.get<DBImportJob>(
    `${catalogCollectionPath(scope, "importJobs")}/${importJobId}`
  );
};

export const getImportJobsHistoryApi = async (
  scope: CatalogScope
): Promise<DBImportJob[]> => {
  return api.get<DBImportJob[]>(
    `${catalogCollectionPath(scope, "importJobs")}?limit=5`
  );
};
